import { h } from 'preact'
import Icon from '../assets'
import Phrase from '../../libs/translationsUtils/phrase'
import UserAvatar from '../User/UserAvatar'
import UserName from '../User/UserName'
import { Badge, User } from './interfaces'
import Pagination from '../ui-components/Pagination'
import { formatLeaderboardPosition, normalizeUserData } from './utils'

interface LeaderboardTableProps {
    usersList: Array<User>,
    currentUser?: User,
    loggedInUserId: number,
    showCurrentlyLoggedInUserSection?: boolean,
    totalPages: number,
    currentPage: number,
    generateNewUrl:(newPage: number) => string
}

export const LeaderboardUserListTable = ({ usersList, currentUser, showCurrentlyLoggedInUserSection, totalPages, currentPage, generateNewUrl, loggedInUserId }: LeaderboardTableProps) => {
  return (
    <div className='leaderboard-table-container'>
      <section className='leaderboard-table'>
        <header>
          <LeaderboardHeaderRow />
        </header>
        {showCurrentlyLoggedInUserSection && <LeaderboardUserRow isLoggedInUser customClass={'leaderboard-row-current-user'} user={currentUser} /> }
        <div className='leaderboard-table-body'>
          {usersList.map(user => { return <LeaderboardUserRow user={user} isLoggedInUser={user.id === loggedInUserId} customClass={user.id === loggedInUserId ? 'highlight-row' : ''} /> }, { currentUser: currentUser })}
          {totalPages > 1 && <Pagination totalPageCount={totalPages} currentPage={currentPage} generateNewUrl={generateNewUrl} />}
        </div>
      </section>
    </div>
  )
}

export const LeaderboardSkeleton = ({ isLoading }: {isLoading: boolean}) => {
  const placeholderArray = [1, 2, 3]
  return (
    <div className='leaderboard-table-container'>
      <section className='leaderboard-table'>
        <header>
          <LeaderboardHeaderRow />
        </header>
        <div className='leaderboard-table-body'>
          {placeholderArray.map(item => { return <LeaderboardPlaceholderRow isLoading={isLoading} leaderboardPosition={item} /> })}
        </div>
      </section>
    </div>
  )
}

export const LeaderboardEmptyMessage = () => (
  <div className='leaderboard-empty-message-container'>
    <div className='leaderboard-empty-message'>
      <h5 className='leaderboard-empty-message-title'>
        <Phrase p='leaderboard.weekly.empty.title' />
      </h5>
      <div><Phrase p='leaderboard.weekly.empty.description' /></div>
    </div>
  </div>
)

const LeaderboardPlaceholderRow = ({ leaderboardPosition, isLoading }: {leaderboardPosition: number, isLoading: boolean}) => {
  return (
    <div className={`leaderboard-row placeholder-row ${isLoading ? 'loading' : ''}`}>
      <div className='ranking-cell right'>
        <Icon icon='medal' customClass='icon-medal is-hidden-S' />
        <span className='ranking-number'> {leaderboardPosition} </span>
      </div>
      <div className='user-cell'>
        <div className='user-details-container'>
          <div className='user-icon-container'><Icon icon='user' width={40} height={40} customClass='icon-user' /></div>
          <div className='user-details'>
            <div className='user-placeholder' />
            <div className='points-mobile' />
          </div>
        </div>
      </div>
      <div className='points-cell'><div className='points-placeholder is-hidden-S' /> <Icon icon='medal' customClass='icon-medal mobile-medal-icon' /> </div>
      <div className='rank-title-cell is-hidden-S is-hidden-tablet'><div className='rank-title-placeholder' /></div>
      <div className='badges-cell is-hidden-S is-hidden-tablet' />
    </div>
  )
}

interface LeaderboardUserRowProps {
    user?: User,
    customClass?: string,
    isLoggedInUser: boolean
}

const LeaderboardUserRow = ({ user, customClass, isLoggedInUser }: LeaderboardUserRowProps) => {
  if (!user) {
    return null
  }
  const { id, name, avatar, points, leaderboardPosition, rank, rankTitle, badgeList } = user
  let userData = normalizeUserData(id, name, avatar, rank, isLoggedInUser)
  let loggedInUserData = normalizeUserData(id, name, avatar, rank, isLoggedInUser, true)
  const leaderboardPositionToDisplay = formatLeaderboardPosition(leaderboardPosition, points)
  const isMedalVisible = leaderboardPosition !== null && leaderboardPosition <= 3
  return (
    <div className={`leaderboard-row ${customClass ?? ''}`}>
      <div className='ranking-cell right'> {isMedalVisible && <Icon icon='medal' customClass={`icon-medal icon-medal-${leaderboardPosition} is-hidden-S`} /> }
        <span className='ranking-number'> {leaderboardPositionToDisplay} </span>
        <div className='logged-in-user-section-ranking-wrapper'>
          <div className='logged-in-user-section-ranking'>{leaderboardPositionToDisplay}</div>
          <div className='logged-in-user-section-ranking-label'><Phrase p='leaderboard.header.ranking' /></div>
        </div>
      </div>
      <div className='user-cell'>
        <div className='user-details-container'>
          <div className='user-icon-container'><UserAvatar user={userData} showRank style={{ display: 'block' }} /></div>
          <div className='user-details'>
            <UserName user={userData} extraClass='username' />
            <UserName user={loggedInUserData} extraClass='logged-in-user-section-username' />
            <div className='points-mobile'>{ <Phrase m='Forum' p='{n} point| {n} points' count={{ n: points.toString() }} /> }</div>
          </div>
        </div>
      </div>
      <div className='points-cell'>
        <div className='points-placeholder is-hidden-S'> {points} </div>
        <div className='logged-in-user-section-points-wrapper'>
          <div className='logged-in-user-section-points'>{points}</div>
          <div className='logged-in-user-section-points-label'><Phrase p='leaderboard.header.points' /></div>
        </div>
        {isMedalVisible && <Icon icon='medal' customClass={`icon-medal mobile-medal-icon icon-medal-${leaderboardPosition}`} />}
      </div>
      <div className='rank-title-cell is-hidden-S is-hidden-tablet'>{rankTitle}</div>
      <div className='badges-cell is-hidden-S is-hidden-tablet'><BadgesColumn badges={badgeList} /></div>
    </div>
  )
}

const LeaderboardHeaderRow = () => (
  <div className='leaderboard-header-row is-hidden-S'>
    <div className='ranking-cell right'><Phrase p='leaderboard.header.ranking' /></div>
    <div className='user-cell'><Phrase p='leaderboard.header.user' /></div>
    <div className='points-cell'><Phrase p='leaderboard.header.points' /></div>
    <div className='rank-title-cell is-hidden-S is-hidden-tablet'><Phrase p='leaderboard.header.rank' /></div>
    <div className='badges-cell is-hidden-S is-hidden-tablet'><Phrase p='leaderboard.header.badges' /></div>
  </div>
)

const BadgesColumn = ({ badges }: { badges?: Array<Badge> }) => {
  if (badges === undefined) {
    return null
  }
  const badgeCount = badges.length
  const numberOfBadgesToDisplay = 5
  return <div className='badges-icon-container'>
    <div>
      {badges.map((badge, idx: number) => {
        return idx < numberOfBadgesToDisplay ? <img className='badge-icon' src={badge.icon} title={badge.title} /> : null
      })}
    </div>
    <div> {badgeCount <= numberOfBadgesToDisplay ? null : `+${badgeCount - numberOfBadgesToDisplay}` }</div>
  </div>
}
